import { OrderDetailViewComponent } from '../../order-management/order-detail-view/order-detail-view.component';
import { OrderFormComponent } from '../../order-management/order-form/order-form.component';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { RmsModule } from '@rms';
import { OrdersDataService } from '../../../../../services/orders-data.service';
import { OrderDocumentPreviewService } from '../../../../../services/order-document-preview.service';
import { OutboundOrderDetailComponent } from '../../outbound-management/outbound-order-detail/outbound-order-detail.component';
import { lastValueFrom } from 'rxjs';
import { Column } from '@interfaces';
import { CustomerCrmService, RmsMessageService, RmsPulldownService } from '@services';

@Component({
  selector: 'app-customer-orders',
  imports: [RmsModule],
  templateUrl: './customer-orders.component.html',
  styleUrl: './customer-orders.component.scss'
})
export class CustomerOrdersComponent implements OnChanges {
  @Input() companyId!: number;
  Orders: any[] = [];
  Loading = false;
  Error = '';
  Search = '';
  Filter = 'all';
  DisplayLimit = 20;
  View: 'cards' | 'table' = 'cards';
  Expanded = new Set<number>();
  Details = new Map<number, any>();
  DetailLoading = new Set<number>();
  DetailErrors = new Map<number, string>();
  Previewing: number | null = null;
  private request = 0;
  isComplete(row:any) { return [7,9].includes(Number(row.OrderStatusID)) || /^(shipped|delivered|cancelled|canceled|complete)$/i.test(row.StatusName || ''); }
  get activeCount() { return this.Orders.filter(row=>!this.isComplete(row)).length; }
  get totalPurchased() { return this.Orders.filter(row=>!/^draft|cancel/i.test(row.StatusName || '')).reduce((sum,row)=>sum+Number(row.OrderTotal || 0),0); }
  get filteredOrders() {
    const term=this.Search.trim().toLowerCase();
    return this.Orders.filter(row=>(this.Filter==='all' || (this.Filter==='open' ? !this.isComplete(row) : this.isComplete(row))) &&
      (!term || [row.OrderID,row.StatusName,row.WarehouseName,row.TruckerName,row.ProNumber,row.PaymentDisplay].some(value=>String(value ?? '').toLowerCase().includes(term))));
  }
  async toggleOrder(row:any) {
    if(this.Expanded.has(row.OrderID)) { this.Expanded.delete(row.OrderID); return; }
    this.Expanded.add(row.OrderID);
    await this.loadDetail(row.OrderID);
  }
  async loadDetail(orderId:number) {
    if(this.Details.has(orderId) || this.DetailLoading.has(orderId)) return;
    const request=this.request;
    this.DetailLoading.add(orderId); this.DetailErrors.delete(orderId);
    try {
      const data=await lastValueFrom(this.ordersData.getOrderDetails(orderId));
      if(request===this.request) this.Details.set(orderId,data);
    } catch { if(request===this.request) this.DetailErrors.set(orderId,'Order items could not be loaded.'); }
    finally { if(request===this.request) this.DetailLoading.delete(orderId); }
  }
  lineAmount(item:any) {
    if(item.NetWeight == null || item.Price == null || item.NumberOfBags == null) return null;
    return Number(item.NetWeight)*Number(item.Price)*Number(item.NumberOfBags);
  }
  async viewDeliveryOrder(row:any) {
    if(this.Previewing) return;
    this.Previewing=row.OrderID; this.Error='';
    try { await this.documents.view(row.OrderID); }
    catch(error:any) { this.Error=error?.error?.error || error?.message || 'Delivery order could not be opened.'; }
    finally { this.Previewing=null; }
  }
  canShip(row:any) { return [5,6,7,8].includes(Number(row.OrderStatusID)) || /^(sent to outbound|sent to warehouse|shipped|on hold)$/i.test(row.StatusName || ''); }
  openShipping(row:any) {
    const companyId=this.companyId;
    const ref=this.pulldown.open(OutboundOrderDetailComponent,{title:'Order #'+row.OrderID+' · Shipping',data:{OrderID:row.OrderID},width:'85vw',maxWidth:'95vw',maxHeight:'97vh',top:'0'});
    ref.afterClosed().subscribe(()=>{if(this.companyId===companyId)this.load();});
  }
  createOrder() {
    const companyId=this.companyId;
    const ref=this.pulldown.open(OrderFormComponent,{title:'Create customer order',data:{CompanyID:companyId},width:'85vw',maxWidth:'95vw',maxHeight:'97vh',top:'0'});
    ref.afterClosed().subscribe(()=>{if(this.companyId===companyId)this.load();});
  }

  Columns: Column[] = [
    { label: 'Order', key: 'OrderID', width: 12, clickable: true, onAction: (row) => this.openOrder(row) },
    { label: 'Status', key: 'StatusName', width: 14 },
    { label: 'Requested Ship', key: 'ProposedPickupDate', type: 'date', width: 14 },
    { label: 'Order Date', key: 'OrderDateDisplay', width: 16 },
    { label: 'Pickup Date', key: 'PickupDateDisplay', width: 14 },
    { label: 'PRO / Load-out Ref.', key: 'ProNumber', width: 15 },
    { label: 'Carrier', key: 'TruckerName', width: 15 },
    { label: 'Warehouse', key: 'WarehouseName', width: 15 },
    { label: 'Bags', key: 'Bags', width: 10 },
    { label: 'Order Total', key: 'OrderTotalDisplay', width: 16 },
    {
      label: 'Payment', key: 'PaymentDisplay', width: 14,
      cellClass: (row) => 'cell-badge status-' + String(row.PaymentDisplay || '').toLowerCase().replace(/\s+/g, '-'),
      SearchType: 'select',
      dropdownSearchOptions: [
        { value: 'Paid', label: 'Paid' },
        { value: 'Waiting for Payment', label: 'Waiting for Payment' },
        { value: 'Processing', label: 'Processing' },
        { value: 'Partially Paid', label: 'Partially Paid' }
      ]
    },
    {
      // Outbound rule for now : PickupDate on or before today -> Shipped; after today -> Ready to Send.
      label: 'Outbound', key: 'OutboundDisplay', width: 14,
      cellClass: (row) => row.OutboundDisplay && row.OutboundDisplay !== '—' ? 'cell-badge status-' + String(row.OutboundDisplay).toLowerCase().replace(/\s+/g, '-') : '',
      SearchType: 'select',
      dropdownSearchOptions: [
        { value: 'Shipped', label: 'Shipped' },
        { value: 'Ready to Send', label: 'Ready to Send' },
        { value: '—', label: '—' }
      ]
    },
  ];

  constructor(
    private crm: CustomerCrmService,
    private message: RmsMessageService,
    private pulldown: RmsPulldownService,
    private ordersData: OrdersDataService,
    private documents: OrderDocumentPreviewService
  ) { }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['companyId']) {
      this.load();
    }
  }

  openOrder(row: any) {
    const ref=this.pulldown.open<any>(row.StatusName==='Draft' ? OrderFormComponent : OrderDetailViewComponent, {title: 'Order #' + row.OrderID, data: { OrderID: row.OrderID }, width: '85vw', maxWidth: '95vw', maxHeight: '92vh', top: '0'});
    const companyId=this.companyId;
    ref.afterClosed().subscribe(()=>{if(this.companyId===companyId) this.load();});
  }

  load() {
    const request = ++this.request;
    this.Orders = []; this.Details.clear(); this.Expanded.clear(); this.DetailLoading.clear(); this.DetailErrors.clear();
    this.DisplayLimit = 20; this.Error = '';
    if (!this.companyId) { this.Loading = false; return; }
    this.Loading = true;
    this.crm.getOrderHistory(this.companyId).subscribe({
      next: (orders) => {
        if(request!==this.request)return;
        this.Loading=false;
        this.Orders = (orders || []).map((o: any) => ({
          ...o,
          PickupDateDisplay: o.PickupDate ? new Date(o.PickupDate).toLocaleDateString(undefined, {timeZone: 'UTC'}) : '—',
          OrderDateDisplay: o.SaleDate ? new Date(o.SaleDate).toLocaleDateString(undefined, {timeZone: 'UTC'}) : '—',
          OrderTotalDisplay: o.OrderTotal != null ? '$' + Number(o.OrderTotal).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—',
          PaymentDisplay: o.DatePaid ? 'Paid' : o.PaymentStatusName || 'Waiting for Payment',
          OutboundDisplay: o.StatusName || (o.Delivered ? 'Delivered' : o.SentToOutbound ? 'Sent to Outbound' : '—')
        })).sort((a:any,b:any)=>Number(b.OrderID)-Number(a.OrderID));
      },
      error: () => { if(request!==this.request)return; this.Loading=false; this.Error='Order history could not be loaded. Please refresh to retry.'; }
    });
  }

  private errorText(err: any): string {
    return err?.error?.error?.message || err?.message || 'Unknown error';
  }
}
